import { Head, router } from "@inertiajs/react"
import {
  CircleAlert,
  Coffee,
  Droplets,
  type LucideIcon,
  Search,
  Smile,
  Utensils,
  Zap,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import {
  EntryDetailSheet,
  type EntryUpdatePayload,
  type ServerEntry,
} from "@/components/entry-detail-sheet"
import {
  EntryTimeline,
  type TimeGroup,
  type TimelineEntry,
} from "@/components/entry-timeline"
import AppLayout from "@/layouts/app-layout"
import { entryPath, historyPath, restoreEntryPath } from "@/routes"

// ── Types ─────────────────────────────────────────────────────────────────────

interface HistoryProps {
  entries: ServerEntry[]
  total_count: number
  page: number
  per_page: number
  q: string | null
  kind: string | null
  start_date: string | null
  end_date: string | null
  available_tags: { id: number; name: string }[]
}

// ── Filter chip config ────────────────────────────────────────────────────────

interface FilterChip {
  label: string
  value: string | null
  icon?: LucideIcon
  iconColor?: string
}

const FILTER_CHIPS: FilterChip[] = [
  { label: "All", value: null },
  {
    label: "Meals",
    value: "meal",
    icon: Utensils,
    iconColor: "var(--fig-entry-meal-icon)",
  },
  {
    label: "Symptoms",
    value: "symptom",
    icon: CircleAlert,
    iconColor: "var(--fig-entry-symptom-icon)",
  },
  {
    label: "Stools",
    value: "stool",
    icon: Droplets,
    iconColor: "var(--fig-entry-stool-icon)",
  },
  {
    label: "Mood",
    value: "mood",
    icon: Smile,
    iconColor: "var(--fig-entry-mood-icon)",
  },
  {
    label: "Drinks",
    value: "drink",
    icon: Coffee,
    iconColor: "var(--fig-entry-beverage-icon)",
  },
  {
    label: "Energy",
    value: "energy",
    icon: Zap,
    iconColor: "var(--fig-entry-mood-icon)",
  },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
}

function entryTitle(entry: ServerEntry): string {
  switch (entry.kind) {
    case "symptom":
      return typeof entry.data.symptom_type === "string" &&
        entry.data.symptom_type
        ? entry.data.symptom_type
        : "Symptom"
    case "meal": {
      const mealType = entry.data.meal_type as string | undefined
      if (mealType) return mealType.charAt(0).toUpperCase() + mealType.slice(1)
      return "Meal"
    }
    case "drink": {
      const drinkType = entry.data.drink_type as string | undefined
      if (drinkType)
        return drinkType.charAt(0).toUpperCase() + drinkType.slice(1)
      return "Drink"
    }
    case "stool": {
      const bristolType = entry.data.bristol_type as number | undefined
      if (bristolType !== undefined) return `Stool · Type ${bristolType}`
      return "Stool"
    }
    case "mood":
      return "Mood check-in"
    case "energy":
      return "Energy check-in"
    default:
      return entry.kind.charAt(0).toUpperCase() + entry.kind.slice(1)
  }
}

function entryDescription(entry: ServerEntry): string | undefined {
  switch (entry.kind) {
    case "meal": {
      const ingredients = entry.data.ingredients as string[] | undefined
      if (ingredients && ingredients.length > 0) {
        return ingredients
          .map((i) => i.charAt(0).toUpperCase() + i.slice(1))
          .join(", ")
      }
      return entry.notes ?? undefined
    }
    case "stool": {
      if (entry.notes) return entry.notes
      const description = entry.data.description as string | undefined
      return description ?? undefined
    }
    case "symptom": {
      if (entry.notes) return entry.notes
      const desc = entry.data.description as string | undefined
      return desc ?? undefined
    }
    default:
      return entry.notes ?? undefined
  }
}

function entryType(entry: ServerEntry): TimelineEntry["type"] {
  if (entry.kind === "drink") {
    const drinkType = entry.data.drink_type as string | undefined
    if (drinkType === "water") return "water"
    return "beverage"
  }
  return entry.kind
}

/** Group entries by calendar date, then by time of day within each date */
function groupEntriesByDate(entries: ServerEntry[]): {
  dateKey: string
  dateLabel: string
  groups: TimeGroup[]
  count: number
}[] {
  const dateMap = new Map<string, ServerEntry[]>()

  for (const entry of entries) {
    const d = new Date(entry.occurred_at)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    const existing = dateMap.get(key)
    if (existing) {
      existing.push(entry)
    } else {
      dateMap.set(key, [entry])
    }
  }

  const today = new Date()
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`

  const result: {
    dateKey: string
    dateLabel: string
    groups: TimeGroup[]
    count: number
  }[] = []

  for (const [key, dayEntries] of dateMap) {
    let dateLabel: string
    if (key === todayKey) {
      dateLabel = "Today"
    } else if (key === yesterdayKey) {
      dateLabel = "Yesterday"
    } else {
      const [year, month, day] = key.split("-").map(Number)
      const d = new Date(year, month - 1, day)
      dateLabel = d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    }

    // Group within the day by time of day
    const timeGroups: Record<string, TimelineEntry[]> = {
      Evening: [],
      Afternoon: [],
      Morning: [],
    }

    for (const entry of dayEntries) {
      const hour = new Date(entry.occurred_at).getHours()
      let groupLabel: string
      if (hour >= 17) {
        groupLabel = "Evening"
      } else if (hour >= 12) {
        groupLabel = "Afternoon"
      } else {
        groupLabel = "Morning"
      }

      const severityValue =
        entry.kind === "symptom" ? entry.data.severity : undefined
      const severity =
        typeof severityValue === "number" || typeof severityValue === "string"
          ? `${severityValue}/5`
          : undefined

      timeGroups[groupLabel].push({
        id: entry.id,
        type: entryType(entry),
        title: entryTitle(entry),
        description: entryDescription(entry),
        severity,
        time: formatTime(entry.occurred_at),
        tags: entry.tags,
      })
    }

    const groups = Object.entries(timeGroups)
      .filter(([, entries]) => entries.length > 0)
      .map(([label, entries]) => ({ label, entries }))

    result.push({ dateKey: key, dateLabel, groups, count: dayEntries.length })
  }

  return result
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function History({
  entries: initialEntries,
  total_count,
  page,
  per_page,
  q,
  kind,
  start_date,
  end_date,
  available_tags = [],
}: HistoryProps) {
  const [allEntries, setAllEntries] = useState<ServerEntry[]>(initialEntries)
  const [currentPage, setCurrentPage] = useState(page)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<ServerEntry | null>(null)
  const [searchValue, setSearchValue] = useState(q ?? "")

  // Track filter fingerprint to reset accumulated entries when filters change
  const filterKey = `${q ?? ""}-${kind ?? ""}-${start_date ?? ""}-${end_date ?? ""}`
  const prevFilterKey = useRef(filterKey)

  // Reset accumulated entries when filters change (new page 1 from server)
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      setAllEntries(initialEntries)
      setCurrentPage(page)
      prevFilterKey.current = filterKey
    } else if (page > currentPage) {
      // New page appended
      setAllEntries((prev) => [...prev, ...initialEntries])
      setCurrentPage(page)
      setIsLoadingMore(false)
    } else if (page === 1) {
      setAllEntries(initialEntries)
      setCurrentPage(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEntries, page, filterKey])

  const navigate = useCallback(
    (overrides: Record<string, string | number | null | undefined>) => {
      const params: Record<string, string> = {}
      const merged = {
        q: q ?? undefined,
        kind: kind ?? undefined,
        start_date: start_date ?? undefined,
        end_date: end_date ?? undefined,
        ...overrides,
      }
      for (const [k, v] of Object.entries(merged)) {
        if (v != null && v !== "") params[k] = String(v)
      }
      router.get(historyPath(), params, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    [q, kind, start_date, end_date],
  )

  // Debounced search
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      navigate({ q: value || null, page: 1 })
    }, 300)
  }

  const handleKindFilter = (selected: string | null) => {
    navigate({ kind: selected, page: 1 })
  }

  const handleStartDate = (value: string) => {
    navigate({ start_date: value || null, page: 1 })
  }

  const handleEndDate = (value: string) => {
    navigate({ end_date: value || null, page: 1 })
  }

  const handleLoadMore = () => {
    setIsLoadingMore(true)
    navigate({ page: currentPage + 1 })
  }

  const handleEntryClick = (id: string) => {
    const found = allEntries.find((e) => e.id === id)
    setSelectedEntry(found ?? null)
  }

  const handleSave = (id: string, payload: EntryUpdatePayload) => {
    router.patch(
      entryPath(id),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { entry: payload } as any,
      {
        preserveScroll: true,
        onSuccess: () => {
          setSelectedEntry(null)
          toast("Entry updated", { duration: 2000 })
        },
      },
    )
  }

  const handleDelete = (id: string) => {
    setSelectedEntry(null)
    router.delete(entryPath(id), {
      preserveScroll: true,
      onSuccess: () => {
        toast("Entry deleted", {
          action: {
            label: "Undo",
            onClick: () => {
              router.post(
                restoreEntryPath(id),
                {},
                {
                  preserveScroll: true,
                  onSuccess: () => toast("Entry restored", { duration: 2000 }),
                },
              )
            },
          },
          duration: 5000,
        })
      },
    })
  }

  const dateGroups = groupEntriesByDate(allEntries)
  const hasMore = currentPage * per_page < total_count

  return (
    <AppLayout>
      <Head title="History" />

      <div className="px-5 pt-5" style={{ paddingBottom: 88 }}>
        {/* Page Title */}
        <div className="mb-4">
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--fig-text-primary)",
            }}
          >
            History
          </h1>
        </div>

        {/* Search Bar */}
        <div className="relative mb-3">
          <Search
            size={16}
            className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2"
            style={{ color: "var(--fig-search-icon)" }}
          />
          <input
            type="text"
            className="search-input w-full py-2.5 pr-4 pl-11"
            placeholder="Search entries..."
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search entries"
          />
        </div>

        {/* Filter Chips */}
        <div
          className="mb-4 flex gap-2 overflow-x-auto pb-1"
          style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
          aria-label="Filter by entry type"
        >
          {FILTER_CHIPS.map((chip) => {
            const isActive =
              kind === chip.value || (chip.value === null && !kind)
            const Icon = chip.icon
            return (
              <button
                key={chip.label}
                className="filter-chip flex items-center gap-1.5 rounded-full px-3 py-1.5"
                style={{
                  background: isActive
                    ? "var(--fig-chip-active-bg)"
                    : "var(--fig-chip-bg)",
                  color: isActive
                    ? "var(--fig-chip-active-text)"
                    : "var(--fig-chip-text)",
                  boxShadow: isActive
                    ? "var(--fig-chip-active-shadow)"
                    : "none",
                  border: isActive
                    ? "none"
                    : "1px solid var(--fig-divider-color)",
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 500,
                }}
                onClick={() => handleKindFilter(chip.value)}
                aria-pressed={isActive}
              >
                {Icon && (
                  <Icon
                    size={13}
                    style={{
                      color: isActive
                        ? "var(--fig-chip-active-text)"
                        : chip.iconColor,
                    }}
                  />
                )}
                {chip.label}
              </button>
            )
          })}
        </div>

        {/* Date Range Filter */}
        <div
          className="mb-5 flex gap-3 rounded-2xl px-3 pt-2.5 pb-3"
          style={{
            background: "var(--fig-card-bg)",
            border: "1px solid var(--fig-divider-color)",
          }}
        >
          <div className="flex-1">
            <label
              htmlFor="start-date"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: 4,
                paddingLeft: 2,
              }}
            >
              From
            </label>
            <input
              id="start-date"
              type="date"
              className="search-input w-full px-3 py-2.5"
              value={start_date ?? ""}
              onChange={(e) => handleStartDate(e.target.value)}
              aria-label="Start date"
            />
          </div>
          <div
            style={{
              width: 1,
              alignSelf: "stretch",
              background: "var(--fig-divider-color)",
              marginTop: 22,
            }}
          />
          <div className="flex-1">
            <label
              htmlFor="end-date"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: 4,
                paddingLeft: 2,
              }}
            >
              To
            </label>
            <input
              id="end-date"
              type="date"
              className="search-input w-full px-3 py-2.5"
              value={end_date ?? ""}
              onChange={(e) => handleEndDate(e.target.value)}
              aria-label="End date"
            />
          </div>
        </div>

        {/* Entry List grouped by date */}
        {allEntries.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No entries found"
            description="Try adjusting your search or filters"
          />
        ) : (
          <div className="flex flex-col gap-4">
            {dateGroups.map(({ dateKey, dateLabel, groups, count }) => (
              <div key={dateKey} className="date-group">
                {/* Date header */}
                <div
                  className="mb-2 flex items-center gap-2"
                  style={{ paddingLeft: 17 }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--fig-text-inactive)",
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                    }}
                  >
                    {dateLabel}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--fig-text-placeholder)",
                    }}
                  >
                    &middot; {count === 1 ? "1 entry" : `${count} entries`}
                  </span>
                </div>

                {/* Timeline for this date */}
                <EntryTimeline
                  groups={groups}
                  onEntryClick={handleEntryClick}
                />
              </div>
            ))}

            {/* Load more */}
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                style={{
                  background: "var(--fig-card-bg)",
                  border: "1px solid var(--fig-card-border)",
                  borderRadius: 14,
                  padding: "12px 0",
                  width: "100%",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--fig-text-secondary, var(--fig-text-inactive))",
                  cursor: isLoadingMore ? "default" : "pointer",
                  opacity: isLoadingMore ? 0.6 : 1,
                  transition: "opacity 150ms ease",
                }}
              >
                {isLoadingMore ? "Loading…" : "Load more"}
              </button>
            )}
          </div>
        )}
      </div>

      <EntryDetailSheet
        entry={selectedEntry}
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        onSave={handleSave}
        onDelete={handleDelete}
        availableTags={available_tags}
      />
    </AppLayout>
  )
}
